import { useReducer } from 'react';
import { useSessionCRUDActions } from '@mm/state/actions/SessionCRUDActions';
import { Dispatch } from 'redux';
import { StoreState } from '@mm/state/store-interfaces';
import { useDispatch } from 'react-redux';
import commonHandler from '@mm/state/errorHandlers/common-handler';

type State = {
  busy?: boolean;
  verifySuccess?: boolean;
  resendSuccess?: boolean;
  error?: number;
  verificationCode?: string;
};

const defaults: State = {
  busy: false,
  verifySuccess: false,
  resendSuccess: false,
  error: undefined,
  verificationCode: '',
};

const actions = {
  verify: 'verify',
  resend: 'resend',
  verifySuccess: 'verifySuccess',
  resendSuccess: 'resendSuccess',
  error: 'error',
  updateVerificationCode: 'updateVerificationCode',
} as const;

type ActionsType = typeof actions;

type Action<T> = { type: ActionsType[keyof ActionsType]; payload?: T };

const reducer = (
  state: State,
  action: Action<boolean | string | number | undefined>,
) => {
  const { type, payload } = action;

  switch (type) {
    case actions.verify:
    case actions.resend:
      return { ...state, busy: true };
    case actions.verifySuccess:
      return { ...state, busy: false, verifySuccess: true };
    case actions.resendSuccess:
      return { ...state, busy: false, resendSuccess: true };
    case actions.error:
      return { ...state, busy: false, error: payload };
    case actions.updateVerificationCode:
      return {
        ...state,
        verificationCode:
          typeof payload === 'string' ? payload.replace(/\D+/g, '') : '',
      };
    default:
      return state;
  }
};

export const useEmailVerificationReducer = () => {
  const reduxDispatch: Dispatch<StoreState> = useDispatch();
  const [state, dispatch] = useReducer(reducer, defaults);
  const sessionCRUDActions = useSessionCRUDActions();

  const updateVerificationCode = (payload?: string) => {
    dispatch({ type: actions.updateVerificationCode, payload });
  };

  const verify = async () => {
    const { verificationCode } = state;
    if (verificationCode && typeof verificationCode === 'string') {
      dispatch({ type: actions.verify });

      try {
        const res = await sessionCRUDActions.validateEmailVerifyCode({
          verificationCode,
        });

        const errorCode = res.errors?.code?.[0];

        if (errorCode) {
          dispatch({ type: actions.error, payload: errorCode });
          return;
        }

        dispatch({ type: actions.verifySuccess });
      } catch (error) {
        commonHandler(reduxDispatch)(error);
        dispatch({ type: actions.error, payload: error });
      }
    }
  };

  const resend = async () => {
    dispatch({ type: actions.resend });

    try {
      const res = await sessionCRUDActions.resendEmailVerifyCode();
      if (res.success) {
        dispatch({ type: actions.resendSuccess });
      }
    } catch (error) {
      commonHandler(reduxDispatch)(error);
      dispatch({ type: actions.error, payload: error });
    }
  };

  return { state, dispatcher: { updateVerificationCode, verify, resend } };
};
